import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Users, GitBranch, ShieldCheck, LogOut, Calendar, Megaphone, ListChecks, ContactRound, Bell, HandCoins, Kanban, Search, Layers, Palette, Inbox, UserPlus, FileText, AlertTriangle, Factory, ShoppingCart, Wrench, Hammer, HardHat, ClipboardCheck, Landmark, Package, Activity, Laptop, BarChart3, Menu, X, LifeBuoy, Truck, Store } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import RaiseTicketFAB from "@/components/RaiseTicketFAB";

const NAV_BY_ROLE = {
  admin: [
    { section: "Executive" },
    { to: "/analytics", label: "Analytics & Perf.", icon: Activity, testid: "nav-analytics" },
    { to: "/finance/ledger", label: "Finance Ledger", icon: Landmark, testid: "nav-finance-ledger" },
    { section: "Admin" },
    { to: "/admin/users", label: "Users", icon: Users, testid: "nav-users" },
    { to: "/admin/hierarchy", label: "Hierarchy", icon: GitBranch, testid: "nav-hierarchy" },
    { to: "/admin/roles", label: "Roles", icon: ShieldCheck, testid: "nav-roles" },
    { to: "/hr/asset-hub", label: "Asset Command", icon: Laptop, testid: "nav-admin-asset-hub" },
    { to: "/settings/holidays", label: "Holidays", icon: Calendar, testid: "nav-holidays" },
    { section: "Sales Oversight" },
    { to: "/sales/leads", label: "Lead Ledger", icon: HandCoins, testid: "nav-sales-leads" },
    { to: "/sales/marketing-leads", label: "Marketing Lead Ledger", icon: Megaphone, testid: "nav-sales-marketing-leads" },
    { to: "/sales/pipeline", label: "Pipeline", icon: Kanban, testid: "nav-sales-pipeline" },
    { to: "/sales/review", label: "Review Queue", icon: Search, testid: "nav-sales-review" },
    { to: "/sales/report", label: "Sales Report", icon: BarChart3, testid: "nav-sales-report" },
    { section: "Design Oversight" },
    { to: "/design/queue", label: "Allocation Queue", icon: Inbox, testid: "nav-design-queue" },
    { to: "/design/projects", label: "All Projects", icon: Layers, testid: "nav-design-projects" },
    { to: "/design/reports", label: "Design Reports", icon: BarChart3, testid: "nav-design-reports" },
    { section: "CRM & Ops" },
    { to: "/crm/direct-onboard", label: "Direct Onboard", icon: UserPlus, testid: "nav-crm-direct-onboard" },
    { to: "/crm/onboarding", label: "Onboarding", icon: UserPlus, testid: "nav-crm-onboarding" },
    { to: "/crm/concerns", label: "Concerns", icon: AlertTriangle, testid: "nav-crm-concerns" },
    { to: "/ops/onboard", label: "Ops Onboard", icon: UserPlus, testid: "nav-ops-onboard" },
    { to: "/ops/quotations", label: "Quotations", icon: FileText, testid: "nav-ops-quotations" },
    { section: "IMOS" },
    { to: "/imos/orders", label: "Manager Console", icon: Factory, testid: "nav-imos-manager" },
    { to: "/imos/progress", label: "Progress Feed", icon: Layers, testid: "nav-imos-progress" },
    { section: "Factory" },
    { to: "/procurement/purchase", label: "Purchase & BOM", icon: ShoppingCart, testid: "nav-purchase" },
    { to: "/procurement/production", label: "Production Floor", icon: Hammer, testid: "nav-production" },
    { to: "/vendors", label: "Vendors", icon: Store, testid: "nav-vendors" },
    { to: "/cnc/tickets", label: "CNC Tickets", icon: Wrench, testid: "nav-cnc" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
    { section: "Site" },
    { to: "/site/projects", label: "Site Execution", icon: HardHat, testid: "nav-site" },
    { to: "/snag/crm", label: "Snag Console", icon: ClipboardCheck, testid: "nav-snag-crm" },
  ],
  "Marketing Manager": [
    { to: "/manager/calendar", label: "Master Calendar", icon: Calendar, testid: "nav-manager-calendar" },
    { to: "/manager/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-manager-campaigns" },
    { to: "/manager/team", label: "Team", icon: Users, testid: "nav-manager-team" },
    { to: "/manager/leads", label: "Team Leads", icon: ContactRound, testid: "nav-manager-leads" },
  ],
  "Marketing Executive": [
    { to: "/executive/agenda", label: "My Agenda", icon: ListChecks, testid: "nav-exec-agenda" },
    { to: "/executive/leads", label: "Log a Lead", icon: ContactRound, testid: "nav-exec-leads" },
  ],
  "Sales Manager": [
    { to: "/sales/leads", label: "Lead Ledger", icon: HandCoins, testid: "nav-sales-leads" },
    { to: "/sales/marketing-leads", label: "Marketing Lead Ledger", icon: Megaphone, testid: "nav-sales-marketing-leads" },
    { to: "/sales/pipeline", label: "Pipeline", icon: Kanban, testid: "nav-sales-pipeline" },
    { to: "/sales/review", label: "Review Queue", icon: Search, testid: "nav-sales-review" },
    { to: "/sales/report", label: "Sales Report", icon: BarChart3, testid: "nav-sales-report" },
  ],
  // Alias — customer's team uses this role name for the same functional workflow.
  "Interior Consultant Manager": [
    { to: "/sales/leads", label: "Lead Ledger", icon: HandCoins, testid: "nav-sales-leads" },
    { to: "/sales/marketing-leads", label: "Marketing Lead Ledger", icon: Megaphone, testid: "nav-sales-marketing-leads" },
    { to: "/sales/pipeline", label: "Pipeline", icon: Kanban, testid: "nav-sales-pipeline" },
    { to: "/sales/review", label: "Review Queue", icon: Search, testid: "nav-sales-review" },
    { to: "/sales/report", label: "Sales Report", icon: BarChart3, testid: "nav-sales-report" },
  ],
  "Sales Executive": [
    { to: "/sales/leads", label: "My Leads", icon: HandCoins, testid: "nav-sales-leads" },
    { to: "/sales/marketing-leads", label: "Marketing Lead Ledger", icon: Megaphone, testid: "nav-sales-marketing-leads" },
    { to: "/sales/pipeline", label: "Pipeline", icon: Kanban, testid: "nav-sales-pipeline" },
  ],
  // Alias — customer's team uses this role name for the same functional workflow.
  "Interior Consultant": [
    { to: "/sales/leads", label: "My Leads", icon: HandCoins, testid: "nav-sales-leads" },
    { to: "/sales/marketing-leads", label: "Marketing Lead Ledger", icon: Megaphone, testid: "nav-sales-marketing-leads" },
    { to: "/sales/pipeline", label: "Pipeline", icon: Kanban, testid: "nav-sales-pipeline" },
  ],
  "Design Head": [
    { to: "/design/queue", label: "Allocation Queue", icon: Inbox, testid: "nav-design-queue" },
    { to: "/design/projects", label: "All Projects", icon: Layers, testid: "nav-design-projects" },
    { to: "/design/reports", label: "Reports", icon: BarChart3, testid: "nav-design-reports" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
    { to: "/settings/holidays", label: "Holidays", icon: Calendar, testid: "nav-holidays" },
  ],
  "Design Manager": [
    { to: "/design/projects", label: "Team Projects", icon: Layers, testid: "nav-design-projects" },
    { to: "/imos/signoff", label: "IMOS Sign-off", icon: ClipboardCheck, testid: "nav-imos-signoff" },
    { to: "/design/reports", label: "Reports", icon: BarChart3, testid: "nav-design-reports" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
  ],
  "Interior Designer": [
    { to: "/design/projects", label: "My Projects", icon: Palette, testid: "nav-design-projects" },
    { to: "/imos/signoff", label: "IMOS Sign-off", icon: ClipboardCheck, testid: "nav-imos-signoff" },
    { to: "/ops/quotations", label: "Quotations", icon: FileText, testid: "nav-ops-quotations" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
    { to: "/design/leaves", label: "My Leaves", icon: Calendar, testid: "nav-design-leaves" },
  ],
  "CRM Manager": [
    { to: "/crm/direct-onboard", label: "Direct Onboard", icon: UserPlus, testid: "nav-crm-direct-onboard" },
    { to: "/crm/onboarding", label: "Onboarding", icon: UserPlus, testid: "nav-crm-onboarding" },
    { to: "/crm/concerns", label: "Concerns", icon: AlertTriangle, testid: "nav-crm-concerns" },
    { to: "/ops/quotations", label: "Quotations & Timeline", icon: FileText, testid: "nav-ops-quotations" },
    { to: "/imos/progress", label: "IMOS Progress", icon: Factory, testid: "nav-imos-progress" },
    { to: "/site/projects", label: "Site Progress", icon: HardHat, testid: "nav-site" },
    { to: "/crm/procurement-overview", label: "Procurement", icon: ShoppingCart, testid: "nav-crm-procurement" },
    { to: "/snag/crm", label: "Snag Console", icon: ClipboardCheck, testid: "nav-snag-crm" },
    { to: "/closure", label: "Closure Desk", icon: Landmark, testid: "nav-closure" },
  ],
  "CRM Executive": [
    { to: "/crm/direct-onboard", label: "Direct Onboard", icon: UserPlus, testid: "nav-crm-direct-onboard" },
    { to: "/crm/onboarding", label: "Onboarding", icon: UserPlus, testid: "nav-crm-onboarding" },
    { to: "/crm/concerns", label: "Concerns", icon: AlertTriangle, testid: "nav-crm-concerns" },
    { to: "/ops/quotations", label: "Quotations & Timeline", icon: FileText, testid: "nav-ops-quotations" },
    { to: "/imos/progress", label: "IMOS Progress", icon: Factory, testid: "nav-imos-progress" },
    { to: "/site/projects", label: "Site Progress", icon: HardHat, testid: "nav-site" },
    { to: "/crm/procurement-overview", label: "Procurement", icon: ShoppingCart, testid: "nav-crm-procurement" },
    { to: "/snag/crm", label: "Snag Console", icon: ClipboardCheck, testid: "nav-snag-crm" },
    { to: "/closure", label: "Closure Desk", icon: Landmark, testid: "nav-closure" },
  ],
  "Imos Manager": [
    { to: "/imos/orders", label: "Backlog & Delegation", icon: Factory, testid: "nav-imos-manager" },
    { to: "/imos/progress", label: "Team Progress", icon: Layers, testid: "nav-imos-progress" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
  ],
  "Imos Executive": [
    { to: "/imos/orders", label: "My Bucket", icon: Factory, testid: "nav-imos-exec" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
  ],
  "Purchase Manager": [
    { to: "/my-projects", label: "My Projects", icon: Layers, testid: "nav-my-projects" },
    { to: "/factory/orders", label: "Factory Orders", icon: Factory, testid: "nav-factory-orders" },
    { to: "/factory/dispatch-report", label: "Dispatch Report", icon: BarChart3, testid: "nav-dispatch-report" },
    { to: "/procurement/purchase", label: "Purchase & BOM", icon: ShoppingCart, testid: "nav-purchase" },
    { to: "/vendors", label: "Vendors", icon: Store, testid: "nav-vendors" },
    { to: "/cnc/tickets", label: "CNC Tickets", icon: Wrench, testid: "nav-cnc" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
  ],
  "Purchase Executive": [
    { to: "/my-projects", label: "My Projects", icon: Layers, testid: "nav-my-projects" },
    { to: "/factory/orders", label: "Factory Orders", icon: Factory, testid: "nav-factory-orders" },
    { to: "/factory/dispatch-report", label: "Dispatch Report", icon: BarChart3, testid: "nav-dispatch-report" },
    { to: "/procurement/purchase", label: "My BOM Queue", icon: ShoppingCart, testid: "nav-purchase" },
    { to: "/vendors", label: "Vendors", icon: Store, testid: "nav-vendors" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
  ],
  "Procurement Manager": [
    { to: "/factory/orders", label: "Factory Orders", icon: Factory, testid: "nav-factory-orders" },
    { to: "/factory/dispatch-report", label: "Dispatch Report", icon: BarChart3, testid: "nav-dispatch-report" },
    { to: "/procurement/purchase", label: "Purchase & BOM", icon: ShoppingCart, testid: "nav-purchase" },
    { to: "/vendors", label: "Vendors", icon: Store, testid: "nav-vendors" },
    { to: "/cnc/tickets", label: "CNC Tickets", icon: Wrench, testid: "nav-cnc" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
  ],
  "Procurement Executive": [
    { to: "/factory/orders", label: "Factory Orders", icon: Factory, testid: "nav-factory-orders" },
    { to: "/factory/dispatch-report", label: "Dispatch Report", icon: BarChart3, testid: "nav-dispatch-report" },
    { to: "/procurement/purchase", label: "Purchase & BOM", icon: ShoppingCart, testid: "nav-purchase" },
    { to: "/vendors", label: "Vendors", icon: Store, testid: "nav-vendors" },
    { to: "/cnc/tickets", label: "CNC Tickets", icon: Wrench, testid: "nav-cnc" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
  ],
  "Production Manager": [
    { to: "/factory/orders", label: "Factory Orders", icon: Factory, testid: "nav-factory-orders" },
    { to: "/factory/dispatch-report", label: "Dispatch Report", icon: BarChart3, testid: "nav-dispatch-report" },
    { to: "/cnc/tickets", label: "CNC Tickets", icon: Wrench, testid: "nav-cnc" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
  ],
  "Production Executive": [
    { to: "/factory/orders", label: "Factory Orders", icon: Factory, testid: "nav-factory-orders" },
    { to: "/factory/dispatch-report", label: "Dispatch Report", icon: BarChart3, testid: "nav-dispatch-report" },
    { to: "/cnc/tickets", label: "CNC Tickets", icon: Wrench, testid: "nav-cnc" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
  ],
  "CNC Designer": [
    { to: "/cnc/tickets", label: "Ticket Queue", icon: Wrench, testid: "nav-cnc" },
    { to: "/procurement/purchase", label: "Factory Feed", icon: ShoppingCart, testid: "nav-purchase" },
  ],
  "Site Supervisor": [
    { to: "/my-projects", label: "My Projects", icon: Layers, testid: "nav-my-projects" },
    { to: "/component-dispatch", label: "Component Dispatch", icon: Truck, testid: "nav-component-dispatch" },
    { to: "/snag/mine", label: "My Snag Fixes", icon: ClipboardCheck, testid: "nav-snag-mine" },
    { to: "/cnc/tickets", label: "Raise CNC Tickets", icon: Wrench, testid: "nav-cnc" },
  ],
  "Execution Manager": [
    { to: "/my-projects", label: "My Projects", icon: Layers, testid: "nav-my-projects" },
    { to: "/factory/orders", label: "Factory Orders", icon: Factory, testid: "nav-factory-orders" },
    { to: "/snag/crm", label: "Snag Console", icon: ClipboardCheck, testid: "nav-snag-crm" },
    { to: "/procurement/purchase", label: "Factory Feed", icon: ShoppingCart, testid: "nav-purchase" },
  ],
  "Execution Executive": [
    { to: "/site/projects", label: "Sites", icon: HardHat, testid: "nav-site" },
  ],
  "Project Manager": [
    { to: "/my-projects", label: "My Projects", icon: Layers, testid: "nav-my-projects" },
    { to: "/component-dispatch", label: "Component Dispatch", icon: Truck, testid: "nav-component-dispatch" },
  ],
  "Finance Manager": [
    { to: "/finance/ledger", label: "Payment Ledger", icon: Landmark, testid: "nav-finance-ledger" },
    { to: "/site/projects", label: "95% Approvals", icon: HardHat, testid: "nav-site" },
    { to: "/closure", label: "Closure Desk", icon: Landmark, testid: "nav-closure" },
  ],
  "Finance Executive": [
    { to: "/finance/ledger", label: "Payment Ledger", icon: Landmark, testid: "nav-finance-ledger" },
    { to: "/closure", label: "Closure Desk", icon: Landmark, testid: "nav-closure" },
  ],
  "HR Manager": [
    { to: "/hr/asset-hub", label: "Asset Command", icon: Laptop, testid: "nav-hr-asset-hub" },
  ],
  "HR Executive": [
    { to: "/hr/asset-hub", label: "Asset Command", icon: Laptop, testid: "nav-hr-asset-hub" },
  ],
  "Branch Manager": [
  ],
  "Operations Manager": [
    { to: "/ops/onboard", label: "Client Onboard", icon: UserPlus, testid: "nav-ops-onboard" },
    { to: "/ops/quotations", label: "Quotations", icon: FileText, testid: "nav-ops-quotations" },
    { to: "/closure", label: "Warranty & Handover", icon: Package, testid: "nav-closure" },
  ],
  "Operations Executive": [
    { to: "/ops/onboard", label: "Client Onboard", icon: UserPlus, testid: "nav-ops-onboard" },
    { to: "/ops/quotations", label: "Quotations", icon: FileText, testid: "nav-ops-quotations" },
    { to: "/closure", label: "Warranty & Handover", icon: Package, testid: "nav-closure" },
  ],
  Director: [
    { section: "Executive" },
    { to: "/analytics", label: "Analytics & Perf.", icon: Activity, testid: "nav-analytics" },
    { to: "/finance/ledger", label: "Finance Ledger", icon: Landmark, testid: "nav-finance-ledger" },
    { section: "Sales" },
    { to: "/sales/leads", label: "Sales Ledger", icon: HandCoins, testid: "nav-sales-leads" },
    { to: "/sales/pipeline", label: "Sales Pipeline", icon: Kanban, testid: "nav-sales-pipeline" },
    { to: "/sales/review", label: "Review Queue", icon: Search, testid: "nav-sales-review" },
    { to: "/sales/report", label: "Sales Report", icon: BarChart3, testid: "nav-sales-report" },
    { section: "Design" },
    { to: "/design/queue", label: "Allocation Queue", icon: Inbox, testid: "nav-design-queue" },
    { to: "/design/projects", label: "All Projects", icon: Layers, testid: "nav-design-projects" },
    { to: "/design/reports", label: "Design Reports", icon: BarChart3, testid: "nav-design-reports" },
    { to: "/settings/holidays", label: "Holidays", icon: Calendar, testid: "nav-holidays" },
    { section: "CRM & Ops" },
    { to: "/crm/direct-onboard", label: "Direct Onboard", icon: UserPlus, testid: "nav-crm-direct-onboard" },
    { to: "/crm/onboarding", label: "Onboarding", icon: UserPlus, testid: "nav-crm-onboarding" },
    { to: "/ops/onboard", label: "Ops Onboard", icon: UserPlus, testid: "nav-ops-onboard" },
    { to: "/ops/quotations", label: "Quotations", icon: FileText, testid: "nav-ops-quotations" },
    { section: "IMOS" },
    { to: "/imos/orders", label: "Backlog", icon: Factory, testid: "nav-imos-manager" },
    { to: "/imos/progress", label: "Progress", icon: Layers, testid: "nav-imos-progress" },
    { to: "/material-requests/inbox", label: "Material Requests", icon: Inbox, testid: "nav-material-requests-inbox" },
  ],
};

export default function DashboardLayout() {
  const { principal, logout, isAdmin, roleName } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const navItems = isAdmin ? NAV_BY_ROLE.admin : (NAV_BY_ROLE[roleName] || (principal?.role?.type === "director" ? NAV_BY_ROLE.Director : []));
  const workspaceLabel = isAdmin ? "Admin" : (roleName || "Workspace");

  const linkClass = ({ isActive }) =>
    `group flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all min-h-[44px] ${
      isActive
        ? "bg-[#d4af37]/10 text-[#d4af37] border-l-2 border-[#d4af37] pl-[14px]"
        : "text-gray-400 hover:text-white hover:bg-[#1c1c1c] border-l-2 border-transparent"
    }`;

  return (
    <div className="min-h-screen flex bg-[#050505]">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 h-14 bg-[#080808] border-b border-[#262626] flex items-center justify-between px-3">
        <button
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="w-11 h-11 rounded-md flex items-center justify-center text-gray-300 hover:text-[#d4af37] hover:bg-[#1c1c1c]"
          data-testid="mobile-menu-open">
          <Menu className="w-5 h-5" strokeWidth={1.5}/>
        </button>
        <img src="/branding/interazzo-logo.png" alt="Interazzo" className="h-8 w-auto object-contain"/>
        <div className="w-11 h-11"/>
      </header>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          data-testid="mobile-menu-backdrop"
        />
      )}

      <aside
        className={`fixed md:static top-0 left-0 z-50 w-64 h-screen md:h-auto border-r border-[#262626] flex flex-col bg-[#080808] transition-transform duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        data-testid="sidebar">
        <div className="p-6 border-b border-[#262626] flex items-center justify-between">
          <img src="/branding/interazzo-logo.png" alt="Interazzo" className="w-40 h-auto object-contain" data-testid="brand-logo" />
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            className="md:hidden w-11 h-11 -mr-2 rounded-md flex items-center justify-center text-gray-300 hover:text-[#d4af37]"
            data-testid="mobile-menu-close">
            <X className="w-5 h-5" strokeWidth={1.5}/>
          </button>
        </div>

        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {navItems.length === 0 && (
            <p className="text-xs text-gray-500 px-4 py-3">No workspace configured for this role yet.</p>
          )}
          {navItems.map((n, idx) => {
            if (n.section) {
              return (
                <p key={`section-${idx}`} className="text-[9px] font-mono uppercase tracking-[0.25em] text-gray-600 px-4 pt-4 pb-1">
                  {n.section}
                </p>
              );
            }
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} className={linkClass} data-testid={n.testid}>
                <Icon className="w-4 h-4" strokeWidth={1.5}/> {n.label}
              </NavLink>
            );
          })}
          {/* Personal — universally available */}
          <>
            <p className="text-[9px] font-mono uppercase tracking-[0.25em] text-gray-600 px-4 pt-4 pb-1">Personal</p>
            {!isAdmin && (
              <NavLink to="/my-assets" className={linkClass} data-testid="nav-my-assets">
                <Package className="w-4 h-4" strokeWidth={1.5}/> My Assets
              </NavLink>
            )}
            <NavLink to="/tickets" className={linkClass} data-testid="nav-tickets">
              <LifeBuoy className="w-4 h-4" strokeWidth={1.5}/> Tickets
            </NavLink>
          </>
        </nav>

        <div className="p-4 border-t border-[#262626]">
          <div className="flex items-center justify-between mb-3 px-2">
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{principal?.name || "User"}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 truncate">{principal?.email}</p>
            </div>
          </div>
          <button onClick={doLogout} className="iz-btn-secondary w-full flex items-center justify-center gap-2 min-h-[44px]" data-testid="logout-button">
            <LogOut className="w-4 h-4" strokeWidth={1.5}/> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden relative pt-14 md:pt-0">
        <div className="absolute top-6 right-8 z-40 flex items-center gap-3" data-testid="header-actions">
          {!isAdmin && <NotificationBell />}
        </div>
        {!location.pathname.startsWith("/tickets") && (
          <div className="w-full flex items-center justify-center py-4 border-b border-[#161616] bg-[#050505]/60 z-20"
               data-testid="raise-ticket-strip">
            <RaiseTicketFAB />
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
